import React, { useEffect, useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { QuizsetSearchReqOrder } from '@/api/gen/_model/quizsetSearchReqOrder';
import { QuizsetSearchReqPeriod } from '@/api/gen/_model/quizsetSearchReqPeriod';
import { ArrowsUpdownIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@/icons';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { Accordion, AccordionContent, AccordionItem, AccordionTriggerNoIcon } from '@/ui_v3/components/accordion';
import { cn } from '@/utils/tailwind.util';
export const SearchOrderDropDownMenu = () => {
  const {
    handleOrder,
    handlePeriod,
    order,
    period
  } = useSearchQuizQuery();
  const {
    t
  } = useTranslation(['common']);
  const [isOpen, setIsOpen] = useState(false);
  const [periodSubMenuOpen, setPeriodSubMenuOpen] = useState<'open' | ''>('');

  // 드롭다운이 열릴 때 인기순 섹션을 자동으로 펼침
  useEffect(() => {
    if (isOpen) {
      setPeriodSubMenuOpen('open');
    }
  }, [isOpen]);
  const 최신순 = t('common:public.index.orderLatest');
  const 인기순 = t('common:public.index.orderBest');
  const periodOptions: {
    name: string;
    value: QuizsetSearchReqPeriod;
  }[] = [{
    name: t('common:range.daily'),
    value: QuizsetSearchReqPeriod.DAY
  }, {
    name: t('common:range.weekly'),
    value: QuizsetSearchReqPeriod.WEEK
  }, {
    name: t('common:range.monthly'),
    value: QuizsetSearchReqPeriod.MONTH
  }, {
    name: t('common:category.all'),
    value: QuizsetSearchReqPeriod.ALL
  }];
  const getPeriodLabel = (periodValue: QuizsetSearchReqPeriod) => {
    return periodOptions.find(p => p.value === periodValue)?.name ?? t('common:range.weekly');
  };
  const getButtonLabel = () => {
    if (order === QuizsetSearchReqOrder.RECENTLY) {
      return 최신순;
    }
    return `${인기순} ${getPeriodLabel(period)}`;
  };
  const handleRecentlyClick = () => {
    logger.track('change_sort', {
      sort: QuizsetSearchReqOrder.RECENTLY
    });
    handleOrder(QuizsetSearchReqOrder.RECENTLY);
    setIsOpen(false);
  };
  const handlePeriodSelect = (periodValue: QuizsetSearchReqPeriod) => {
    logger.track('change_period', {
      period: periodValue
    });
    logger.track('change_sort', {
      sort: QuizsetSearchReqOrder.POPULARITY
    });
    handleOrder(QuizsetSearchReqOrder.POPULARITY);
    handlePeriod(periodValue);
    setIsOpen(false);
    setPeriodSubMenuOpen('');
  };
  const isRecentlySelected = order === QuizsetSearchReqOrder.RECENTLY;
  const isPopularitySelected = order === QuizsetSearchReqOrder.POPULARITY;
  return <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false} data-sentry-element="DropdownMenu" data-sentry-component="SearchOrderDropDownMenu" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
      <DropdownMenuTrigger className={cn('flex items-center rounded-sm focus:outline-none')} tabIndex={-1} asChild={true} data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
        <button className={cn('flex h-[36px] items-center gap-3xs rounded-2xs bg-white px-sm text-body-md-medium', 'transition-colors hover:bg-gray-50')}>
          <ArrowsUpdownIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="ArrowsUpdownIcon" data-sentry-source-file="SearchOrderDropDownMenu.tsx" />
          {getButtonLabel()}
          <ChevronDownIcon size="xs" fill={ICON_COLOR.neutral} className={cn('transition-transform', isOpen && 'rotate-180')} data-sentry-element="ChevronDownIcon" data-sentry-source-file="SearchOrderDropDownMenu.tsx" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end"
    // GNB(HeaderContainer, z-40) 아래로 깔리도록 z-30 으로 기본 z-50 을 override 한다.
    className={cn('z-30 max-h-[800px] min-w-[205px] rounded-sm px-xs py-3xs', 'shadow-[0_4px_8px_2px_rgba(28,27,31,0.12)]')} data-sentry-element="DropdownMenuContent" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
        {/* 인기순 with nested accordion for period */}
        <Accordion value={periodSubMenuOpen} collapsible type="single" onValueChange={value => setPeriodSubMenuOpen(value as 'open' | '')} data-sentry-element="Accordion" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
          <AccordionItem value="open" className="border-none" data-sentry-element="AccordionItem" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
            <AccordionTriggerNoIcon className={cn('h-[36px] min-h-[36px] gap-xs rounded-2xs px-xs py-none', 'hover:bg-gray-50 focus:bg-gray-50', isPopularitySelected ? 'bg-primary-subtle text-primary' : 'text-default')} data-sentry-element="AccordionTriggerNoIcon" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
              <div className="flex h-full w-full cursor-pointer items-center justify-between self-stretch">
                <div className="flex flex-1 items-center gap-xs">
                  <div className="text-body-md-medium">{인기순}</div>
                  {isPopularitySelected && <CheckIcon size="2xs" fill="currentColor" />}
                </div>
                <ChevronUpIcon className={cn('transform transition-transform', periodSubMenuOpen === 'open' ? '' : 'rotate-180')} size="xs" fill={ICON_COLOR.neutral} data-sentry-element="ChevronUpIcon" data-sentry-source-file="SearchOrderDropDownMenu.tsx" />
              </div>
            </AccordionTriggerNoIcon>
            <AccordionContent className="max-h-[240px] overflow-scroll bg-neutral px-2xs pb-none pt-2xs" data-sentry-element="AccordionContent" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
              {periodOptions.map(option => <DropdownMenuItem key={option.value} className={cn('flex justify-between pr-2xs text-body-md-medium', period === option.value && isPopularitySelected ? 'bg-primary-subtle text-primary' : 'text-default')} onClick={() => handlePeriodSelect(option.value)}>
                  <span>{option.name}</span>
                  {period === option.value && isPopularitySelected && <CheckIcon size="2xs" fill="currentColor" />}
                </DropdownMenuItem>)}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* 최신순 */}
        <DropdownMenuItem className={cn('flex justify-between pr-2xs text-body-md-medium', isRecentlySelected ? 'bg-primary-subtle text-primary' : 'text-default')} onClick={handleRecentlyClick} data-sentry-element="DropdownMenuItem" data-sentry-source-file="SearchOrderDropDownMenu.tsx">
          <span>{최신순}</span>
          {isRecentlySelected && <CheckIcon size="2xs" fill="currentColor" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>;
};